<template>
  <div class="categories-strip__wrapper">
    <a-swiper
      v-if="isSwiperVisible"
      :swipeList="categoryListToDisplay"
      :customOptions="options"
      class="categories-strip__swiper__wrapper"
    >
      <categories-strip-item
        slot-scope="{ slide, index }"
        class="swiper__categories-strip__item"
        :item="slide"
        :item-index="index"
        :thought-leader-ship-route="thoughtLeaderShipRoute"
        :link="generateLink(slide)"
      />

      <div class="swiper-button" slot="swiper-prev"></div>
      <div class="swiper-button" slot="swiper-next"></div>
    </a-swiper>

    <div class="categories-strip__container" v-else>
      <ul class="categories-strip">
        <li
          v-for="(item, index) in categoryListToDisplay"
          :key="`${item}-${index}`"
          class="categories-strip__item"
        >
          <categories-strip-item
            :item="item"
            :item-index="index"
            :thought-leader-ship-route="thoughtLeaderShipRoute"
            :link="generateLink(item)"
          />
        </li>
        <li key="slot" class="categories-strip__slot">
          <slot />
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { prop } from 'ramda'

import { propValidator, PROP_TYPES } from '@/utils/validators'
import { SPECIAL_CATEGORY } from 'enums/special-categories'
import ASwiper from 'shared/ASwiper'
import CategoriesStripItem from './CategoriesStripItem'
import { ROUTE_NAME } from 'enums/routes'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'
import { serverCacheKey } from '@/utils/mixins/serverCacheKey'

const PAGE_TYPE = {
  ALL_NEWS: 'allNews',
  CATEGORY: 'category',
  SUB_CATEGORY: 'subCategory'
}

export const CATEGORY_STRIP_ITEM_TYPE = {
  ALL_NEWS: 'allNews',
  CATEGORY: 'category',
  SUB_CATEGORY: 'subCategory',
  DIVIDER: 'divider'
}

export default {
  name: 'ACategoriesStrip',
  mixins: [hydrationHelpers, serverCacheKey],
  components: { CategoriesStripItem, ASwiper },
  props: {
    pageType: propValidator([PROP_TYPES.STRING]),
    categoryName: propValidator([PROP_TYPES.STRING], false, ''),
    subcategoryName: propValidator([PROP_TYPES.STRING], false, ''),
    categoriesStrip: propValidator([PROP_TYPES.ARRAY])
  },
  data() {
    return {
      category: this.$route.params.category,
      thoughtLeaderShipRoute: {
        Name: 'Thought Leadership',
        Slug: SPECIAL_CATEGORY.THOUGHT_LEADERSHIP
      }
    }
  },
  computed: {
    isSwiperVisible() {
      return (
        this.categoriesStrip.length &&
        this.$_hydrationHelpers_isVisibleOnRange([this.$breakpoint.tablet])
      )
    },
    categoryListToDisplay() {
      return this.$_hydrationHelpers_isLayoutMobile
        ? this.categoryListFormatted.slice(0, 1)
        : this.categoryListFormatted
    },
    options() {
      return {
        slidesPerView: 'auto',
        slidesPerGroup: 1
      }
    },
    currentPageName() {
      return this.subcategoryName || this.categoryName || 'All News'
    },
    isThoughtLeadership() {
      return this.category === this.thoughtLeaderShipRoute.Slug
    },
    /**
     * If current page is not ThoughtLeadership and not a subcategory page, the last strip item should be
     * of ThoughtLeadership type
     */
    isLastStripItemThoughtLeadership() {
      return (
        !this.isThoughtLeadership && this.pageType !== PAGE_TYPE.SUB_CATEGORY
      )
    },
    categoryListFormatted() {
      /**
       * First item of a strip
       */
      const categoryListFormatted = [
        {
          Type: this.pageType,
          Name: this.currentPageName,
          Slug:
            this.pageType === PAGE_TYPE.SUB_CATEGORY
              ? this.$route.params.subcategory
              : this.$route.params.category
        }
      ]

      this.categoriesStrip.forEach(category => {
        categoryListFormatted.push({ Type: CATEGORY_STRIP_ITEM_TYPE.DIVIDER })

        /**
         * According to business logic, on All News page there are only parent categories, whereas
         * on Category page, all items after first one are subcategories
         */
        categoryListFormatted.push({
          ...category,
          Type:
            this.pageType === PAGE_TYPE.ALL_NEWS
              ? CATEGORY_STRIP_ITEM_TYPE.CATEGORY
              : CATEGORY_STRIP_ITEM_TYPE.SUB_CATEGORY
        })
      })

      if (this.isLastStripItemThoughtLeadership) {
        categoryListFormatted.push({ Type: CATEGORY_STRIP_ITEM_TYPE.DIVIDER })
        categoryListFormatted.push({
          ...this.thoughtLeaderShipRoute,
          Type: CATEGORY_STRIP_ITEM_TYPE.CATEGORY
        })
      }

      return categoryListFormatted
    }
  },
  methods: {
    prop,
    generateLink(item) {
      switch (item.Type) {
        case CATEGORY_STRIP_ITEM_TYPE.ALL_NEWS:
          return {
            name: ROUTE_NAME.ALL_NEWS
          }
        case CATEGORY_STRIP_ITEM_TYPE.CATEGORY:
          return {
            name: 'category',
            params: {
              category: prop('Slug', item)
            }
          }
        case CATEGORY_STRIP_ITEM_TYPE.SUB_CATEGORY:
          return {
            name: 'category-subcategory',
            params: {
              category: this.category,
              subcategory: prop('Slug', item)
            }
          }
        default:
          return {}
      }
    }
  }
}
</script>

<style lang="scss">
.categories-strip__wrapper {
  margin: 40px 0 20px 0;
  width: 100%;

  @include mobile {
    margin: 20px 0 0;
  }

  .categories-strip__swiper__wrapper {
    width: calc(100% + 15px);
  }

  .swiper__custom-swiper {
    width: 100%;
    margin: 0;
  }

  .swiper__list__item {
    width: auto;

    &:nth-child(2n) {
      margin: auto;

      @include tablet {
        margin: auto 0;
      }
    }
  }
}

.categories-strip__container {
  max-width: 1450px;

  @include mobile {
    margin: 20px 0 0;
  }
}

.categories-strip {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.categories-strip__item-text {
  font-size: 19px;
  white-space: nowrap;
  text-transform: capitalize;
}

.categories-strip__slot {
  margin-left: auto;

  @include mobile {
    margin-left: 0;
    width: 100%;
    text-align: right;
  }
}

.categories-strip__wrapper .swiper__list__item,
.categories-strip__item {
  font-weight: bold;

  &:nth-child(2n) {
    padding: 0 30px;
  }

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    font-weight: normal;
  }

  &:first-child,
  &:last-child {
    color: $c--main;
  }

  &:last-child .categories-strip__item-text {
    font-weight: normal;

    color: $c--main;
  }

  &:first-child .categories-strip__item-text {
    font-weight: bold;
    color: $c--main;
  }
}
</style>
